import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './admin-readability.css';
import {
  Home, Users, DollarSign, Shield, Bell, FileText, Settings, LogOut,
  Crosshair, ClipboardList, Package, ChevronDown, ChevronRight, Flag,
  Calendar, GraduationCap, Building2, UserPlus, Bug, Menu, X, Command, Smartphone, Lock, Activity, ArrowUp, Mail, Handshake, Wrench, LifeBuoy, Award, Gift, Ticket, Rocket, Gem, Radar, Wallet, KeyRound
} from 'lucide-react';
import { apiClient } from '../../api/client';
import CommandPalette from '../admin/CommandPalette';
import LiveActivityPulse from '../admin/LiveActivityPulse';
import ResponsivePreview from '../admin/ResponsivePreview';
import AuthDriftBanner from '../admin/AuthDriftBanner';
import VaultLockedChip from '../admin/VaultLockedChip';
import '../../styles/admin-readability.css';

const DateRangeContext = createContext({ dateRange: '30d', setDateRange: () => {} });
export const useDateRange = () => useContext(DateRangeContext);

const RANGE_OPTIONS = [
  { id: '7d', label: 'Last 7 Days' },
  { id: '14d', label: 'Last 14 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: '90d', label: 'Last 90 Days' },
  { id: 'all', label: 'All Time' },
];

// domain: which admin_domain grants access to this item
// null = admin-only (master admin)
const NAV_GROUPS = [
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { id: 'home', label: 'Analytics', icon: Home, path: '/admin', domain: 'analytics' },
      { id: 'users', label: 'Users', icon: Users, path: '/admin/users', domain: 'users' },
      { id: 'leads', label: 'Lead Generation', icon: Crosshair, path: '/admin/leads', domain: 'leads' },
      // Feb 2026 — Robert flagged that the pre-launch waitlist page
      // (`/admin/waitlist-broadcast`) had no nav entry so it was
      // effectively hidden from the CC. Same "leads" domain so any
      // sub-admin who can see Lead Generation can see waitlist signups.
      { id: 'waitlist', label: 'Waitlist Signups', icon: Mail, path: '/admin/waitlist-broadcast', domain: 'leads' },
      { id: 'welcome-pack', label: 'Welcome Pack Leads', icon: Gift, path: '/admin/welcome-pack-leads', domain: 'leads' },
      { id: 'revenue', label: 'Revenue & Subs', icon: DollarSign, path: '/admin/revenue', domain: 'analytics' },
      { id: 'nft-economy', label: 'NFT Economy', icon: Gem, path: '/admin/nft-economy', domain: 'analytics' },
    ],
  },
  {
    id: 'safety',
    label: 'Safety & Compliance',
    items: [
      { id: 'safety', label: 'Safety & Guardrails', icon: Shield, path: '/admin/safety', domain: 'users' },
      { id: 'ops-alerts', label: 'Ops Radar', icon: Radar, path: '/admin/ops-alerts', domain: 'users' },
      { id: 'trust-safety', label: 'Trust & Safety (NeMo)', icon: Shield, path: '/admin/trust-safety', domain: 'users' },
      { id: 'security', label: 'Security', icon: Lock, path: '/admin/security', domain: null },
      { id: 'secrets', label: 'API Keys', icon: KeyRound, path: '/admin/secrets', domain: null },
      { id: 'audit', label: 'Audit Trail', icon: ClipboardList, path: '/admin/audit', domain: null },
      { id: 'logs', label: 'System Logs', icon: Activity, path: '/admin/logs', domain: null },
    ],
  },
  {
    id: 'content',
    label: 'Content & Features',
    items: [
      { id: 'content', label: 'Content Manager', icon: Package, path: '/admin/content', domain: 'content' },
      { id: 'features', label: 'Feature Flags', icon: Flag, path: '/admin/features', domain: null },
      { id: 'notifications', label: 'Notifications', icon: Bell, path: '/admin/notifications', domain: 'content' },
    ],
  },
  {
    id: 'b2b',
    label: 'B2B Partners',
    items: [
      { id: 'schools', label: 'Schools', icon: GraduationCap, path: '/admin/schools', domain: 'schools' },
      { id: 'schools-engine', label: 'Schools Engine', icon: Rocket, path: '/admin/schools-engine', domain: 'schools' },
      { id: 'corporates', label: 'Corporates', icon: Building2, path: '/admin/corporates', domain: 'corporates' },
      { id: 'partner-leads', label: 'B2B Leads', icon: Handshake, path: '/admin/partner-leads', domain: 'leads' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'reports', label: 'Reports', icon: FileText, path: '/admin/reports', domain: null },
      { id: 'maintenance', label: 'Maintenance Crew', icon: Wrench, path: '/admin/maintenance', domain: null },
      { id: 'team', label: 'Team', icon: UserPlus, path: '/admin/team', domain: null },
      { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings', domain: null },
      { id: 'demo-codes', label: 'Demo Codes', icon: Ticket, path: '/admin/demo-codes', domain: null },
      { id: 'vault', label: 'Secure Vault', icon: Lock, path: '/admin/vault', domain: null },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { id: 'help-requests', label: 'Help Requests (Maya)', icon: LifeBuoy, path: '/admin/help-requests', domain: null },
      { id: 'bug-reports', label: 'Bug Bounty', icon: Award, path: '/admin/bug-reports', domain: null },
      { id: 'dev-support', label: 'Developer Support', icon: Bug, path: '/admin/developer-support', domain: '_all_subadmins' },
    ],
  },
];

// Domain-to-section mapping (mirrors backend ADMIN_DOMAINS)
const DOMAIN_SECTIONS = {
  schools: ['schools', 'reports'],
  corporates: ['corporates', 'reports'],
  users: ['users', 'safety', 'reports'],
  leads: ['leads'],
  content: ['content', 'notifications'],
  analytics: ['home', 'revenue', 'reports'],
};

const AdminLayout = ({ children, pageTitle = 'Command Centre' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Embed mode: when this page is rendered inside the Vault iframe (Schools
  // tab, Pitch Deck tab), the URL carries ?embed=1 so we render just the
  // children without the sidebar/topbar/chrome — the parent Vault already
  // shows the founder navigation, and a nested sidebar inside an iframe
  // creates a confusing "menu inside a menu" effect.
  const isEmbedded = (() => {
    try {
      return new URLSearchParams(location.search).get('embed') === '1';
    } catch {
      return false;
    }
  })();

  const [dateRange, setDateRange] = useState('30d');
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Sprint 179 — race-condition fix.
  // Seed userRole / adminDomain from sessionStorage so a cache-cleared cold
  // load still has values to filter against during the /api/users/me round
  // trip, otherwise NAV_GROUPS.filter() drops every item and the sidebar
  // disappears for ~300 ms (visible as "menu briefly missing on first load").
  const _readSess = (k) => { try { return sessionStorage.getItem(k) || null; } catch { return null; } };
  const _cachedRole = _readSess('hibff_admin_role');
  const _cachedDomain = _readSess('hibff_admin_domain');
  const [userRole, setUserRole] = useState(_cachedRole);
  const [adminDomain, setAdminDomain] = useState(_cachedDomain);
  const [roleLoading, setRoleLoading] = useState(!_cachedRole);
  const [userName, setUserName] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Read scroll position from whichever element is actually scrolling.
    // Most admin pages scroll the window, but a few layouts have
    // pushed `overflow-y:auto` onto inner wrappers in the past which
    // left `window.scrollY === 0` even after the user clearly scrolled
    // — making the button invisible. Read from the union to be safe.
    const getScrollY = () => Math.max(
      window.scrollY || 0,
      window.pageYOffset || 0,
      document.documentElement?.scrollTop || 0,
      document.body?.scrollTop || 0,
    );
    const onScroll = () => setShowScrollTop(getScrollY() > 320);
    // Run once on mount so the button is correctly visible on a deep
    // link / hash navigation that loads already scrolled.
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    // Also listen on document — some Capacitor / webview setups
    // deliver the event there instead of window.
    document.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollAllToTop = () => {
    // Native smooth scroll for the primary window. The imperative
    // documentElement/body writes below used to run REGARDLESS of the
    // smooth call above, which cancelled the animation and made the
    // scroll-to-top button jump instantly (reported Feb 2026). Now
    // we only fall back to imperative scroll if the window itself
    // isn't scrollable — e.g. when the page is embedded inside a
    // scrollable container.
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
    if ((window.scrollY || 0) === 0) return;
    // Only touch documentElement/body if smooth didn't take effect.
    // (Older Chromium inside iframes occasionally ignores the smooth
    // option; the imperative fallback catches that edge case.)
    setTimeout(() => {
      if ((window.scrollY || 0) === 0) return;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }, 500);
  };

  const handleSignOut = () => {
    apiClient.clearToken();
    // Sprint 182 — also tell the backend to clear the same-origin `hibff_auth`
    // cookie that the admin-direct flow sets, so the redundant auth channel
    // doesn't outlive the explicit sign-out. Fire-and-forget — the user is
    // being redirected anyway.
    try { apiClient.request('/api/auth/logout', { method: 'POST' }); } catch {}
    // Also clear the admin-direct trusted-device cookie proactively (cookie is HttpOnly so
    // we can't remove it from JS — browser will forget it after the server re-issues on next
    // login, or it will simply expire after 30 days). Navigating to /api/auth/admin-direct
    // gives the user a clean slate.
    window.location.replace('/api/auth/admin-direct');
  };

  useEffect(() => {
    apiClient.request('/api/users/me').then(data => {
      setUserRole(data.role);
      setAdminDomain(data.admin_domain || null);
      setUserName(data.name || '');
      try {
        sessionStorage.setItem('hibff_admin_role', data.role || '');
        sessionStorage.setItem('hibff_admin_domain', data.admin_domain || '');
      } catch {}
      setRoleLoading(false);
    }).catch(() => { setRoleLoading(false); });
  }, []);

  const isMasterAdmin = userRole === 'admin';
  const allowedSections = isMasterAdmin ? null : DOMAIN_SECTIONS[adminDomain] || [];

  // Keyboard shortcut for Command Palette (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Filter nav items based on role/domain.
  // Sprint 179 — while the role round-trip is in flight (and we have no
  // cached role), render NAV_GROUPS unfiltered so the sidebar stays visible.
  // The pages themselves enforce server-side permissions, and once role
  // arrives (~300 ms) the filter takes over.
  const filteredGroups = (roleLoading && !userRole ? NAV_GROUPS : NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (isMasterAdmin) return true;
      // '_all_subadmins' items are visible to all sub-admins
      if (item.domain === '_all_subadmins') return userRole === 'sub_admin';
      // sub_admin: only show items in their domain sections, or items with matching domain
      if (item.domain === null) return false; // admin-only items
      return allowedSections.includes(item.id);
    }),
  }))).filter(g => g.items.length > 0);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (group) => group.items.some(item => isActive(item.path));

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const currentRangeLabel = RANGE_OPTIONS.find(r => r.id === dateRange)?.label || 'Last 30 Days';

  const handleAdminNav = (path) => {
    navigate(path);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  return (
    isEmbedded ? (
      <div className="admin-layout-root min-h-screen bg-white" data-testid="admin-layout-embedded">
        <main className="p-3 sm:p-6 min-w-0 max-w-full overflow-x-hidden">{children}</main>
      </div>
    ) :
    <DateRangeContext.Provider value={{ dateRange, setDateRange }}>
      <div className="admin-layout-root flex min-h-screen relative overflow-x-hidden" style={{ backgroundColor: '#F9F9F9' }}>
        {/* Sprint 182 — Auth Drift Banner. Renders only when apiClient
            detects a 401 on an admin/vault endpoint immediately following a
            successful /api/users/me — the exact signature of the
            "Command Centre empty" bug. Sticky-positioned so it floats above
            the sidebar overlay. */}
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 70 }}>
          <AuthDriftBanner />
        </div>
        {/* Sprint 182 — Vault Locked Chip. Subtle informational chip in
            bottom-right when a /api/vault/* endpoint 401s (i.e. TOTP gate
            closed). Distinct from the orange drift banner — this is
            purely informational, not alarming. */}
        <VaultLockedChip />
        {/* Persistent dark column behind the fixed sidebar — guarantees the
            black extends the FULL document height even when right-column
            content (e.g. Investors grid) scrolls past the sidebar's natural
            height. Hidden below lg so tablets + mobile keep their clean light bg. */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute left-0 top-0 bottom-0 z-0"
          style={{ width: '260px', backgroundColor: '#1A1209' }}
          data-testid="admin-sidebar-backdrop"
        />
        {/* Mobile + tablet overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`fixed left-0 top-0 h-full flex flex-col transition-transform duration-300 z-40 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} style={{ width: '260px', backgroundColor: '#1A1209' }}>
          {/* Mobile + tablet close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-3 text-white/60 hover:text-white transition-colors z-50"
            aria-label="Close admin menu"
          >
            <X size={22} />
          </button>

          {/* Logo card — matches the Parent / Teen dashboard treatment so
              the HiBFF mark + ® symbol pop against the dark sidebar. */}
          <div className="mx-3 mt-3 mb-6 px-4 py-6 rounded-2xl flex flex-col items-center" style={{ background: 'rgba(255, 248, 240, 0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.18)' }}>
            <button
              onClick={() => handleAdminNav('/admin')}
              className="w-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="admin-sidebar-logo-btn"
            >
              <img src="/hibff-logo.png" alt="HiBFF" className="h-44 w-auto" />
            </button>
            <p className="text-sm font-semibold text-center tracking-wide mt-2" style={{ color: '#1A1209' }} data-testid="admin-sidebar-brand-line">
              Hi Brave Friends Forever
            </p>
            <p className="text-xs italic text-center px-1 leading-snug mt-1" style={{ color: '#5C4A2E' }} data-testid="admin-sidebar-brand-tagline">
              Brave takes courage. Courage builds confidence. Confidence builds character.
            </p>
            <p className="text-xs font-bold uppercase tracking-wider text-center mt-3" style={{ color: '#F5831F' }} data-testid="admin-sidebar-role-line">
              {isMasterAdmin ? 'Command Centre' : adminDomain ? DOMAIN_SECTIONS[adminDomain] ? `${adminDomain.charAt(0).toUpperCase() + adminDomain.slice(1)} Admin` : 'Sub-Admin' : 'Command Centre'}
            </p>
          </div>

          <nav className="flex-1 py-2 overflow-y-auto">
            {filteredGroups.map(group => {
              const isCollapsed = collapsedGroups[group.id] && !isGroupActive(group);
              return (
                <div key={group.id} className="mb-1">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                    style={{ color: isGroupActive(group) ? '#F5831F' : '#E8D9C0' }}
                    data-testid={`admin-group-${group.id}`}
                  >
                    <span>{group.label}</span>
                    {isCollapsed
                      ? <ChevronRight size={12} style={{ color: '#E8D9C0' }} />
                      : <ChevronDown size={12} style={{ color: '#E8D9C0' }} />
                    }
                  </button>
                  {!isCollapsed && group.items.map(item => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleAdminNav(item.path)}
                        data-testid={`admin-nav-${item.id}`}
                        className="w-full flex items-center gap-3 px-5 py-2 text-sm transition-colors relative"
                        style={{
                          color: active ? '#F5831F' : '#FAF4E6',
                          backgroundColor: active ? 'rgba(245,131,31,0.12)' : 'transparent',
                        }}
                      >
                        {active && <span className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r" style={{ backgroundColor: '#F5831F' }} />}
                        <Icon size={16} />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          <div className="px-5 py-4 border-t border-white/10">
            <button
              onClick={() => {
                try { apiClient.clearToken(); } catch {}
                navigate('/');
              }}
              className="flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
              style={{ color: '#FAF4E6' }}
              data-testid="admin-sign-out-btn"
              title="Sign out of the Command Centre"
            >
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </aside>

        {/* Main Area */}
        <div className="flex-1 lg:ml-[260px] min-w-0 overflow-x-hidden">
          {/* Top Bar */}
          <header className="sticky top-0 bg-white flex items-center justify-between px-4 sm:px-6 py-3" style={{ borderBottom: '1px solid #E0E0E0', zIndex: 30 }}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Open admin menu"
                data-testid="admin-mobile-menu"
              >
                <Menu size={22} className="text-gray-700" />
              </button>
              <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#0A0A0A' }}>{pageTitle}</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Command Palette trigger */}
              <button
                onClick={() => setShowCommandPalette(true)}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-200 transition-colors"
                data-testid="cmd-k-trigger"
              >
                <Command size={12} />
                <span>Search...</span>
                <kbd className="px-1 py-0.5 text-xs bg-gray-100 border border-gray-200 rounded ml-2">K</kbd>
              </button>

              {/* Live Activity Pulse */}
              <LiveActivityPulse />

              {/* Responsive Preview */}
              <button
                onClick={() => setShowPreview(true)}
                className="p-1.5 rounded-lg text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                title="Responsive Preview"
                data-testid="preview-trigger"
              >
                <Smartphone size={16} />
              </button>

              <div className="relative">
                <button onClick={() => setShowRangeDropdown(!showRangeDropdown)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm whitespace-nowrap"
                  style={{ borderColor: '#E0E0E0', color: '#333333' }}
                  data-testid="date-range-picker">
                  <Calendar size={14} style={{ color: '#F5831F' }} />
                  <span className="hidden sm:inline">{currentRangeLabel}</span>
                  <ChevronDown size={14} />
                </button>
                {showRangeDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowRangeDropdown(false)} />
                    <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border z-20" style={{ borderColor: '#E0E0E0', minWidth: '160px' }}>
                      {RANGE_OPTIONS.map(opt => (
                        <button key={opt.id} onClick={() => { setDateRange(opt.id); setShowRangeDropdown(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                          style={{ color: dateRange === opt.id ? '#F5831F' : '#333333', fontWeight: dateRange === opt.id ? 600 : 400 }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 relative">
                {/* Sprint 180 — resolved-identity badge.
                    Shows the role + admin_domain returned by /api/users/me so
                    the founder can instantly verify whether they've landed on
                    the master cockpit or a restricted sub-admin scope. */}
                {!roleLoading && (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                    style={isMasterAdmin
                      ? { background: '#E6F5E1', color: '#1F5C44', border: '1px solid #4CAF50' }
                      : { background: '#FFE3D6', color: '#8C3A20', border: '1px solid #F5831F' }}
                    title={isMasterAdmin
                      ? 'Master Admin — full Command Centre access'
                      : `Sub-Admin restricted to: ${adminDomain || 'unscoped'}`}
                    data-testid="admin-identity-badge"
                  >
                    <Shield size={10} />
                    {isMasterAdmin ? 'Master Admin' : `Sub-Admin · ${adminDomain || 'unscoped'}`}
                  </span>
                )}
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 rounded-lg hover:bg-gray-100 px-2 py-1 transition-colors"
                  data-testid="admin-user-menu-trigger"
                  aria-label="Open account menu"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: '#F5831F' }}>RP</div>
                  <span className="text-sm font-medium hidden sm:inline" style={{ color: '#333333' }}>Robert Parson</span>
                  <ChevronDown size={14} className="hidden sm:inline" style={{ color: '#6B6B6B' }} />
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border z-20 overflow-hidden" style={{ borderColor: '#E0E0E0', minWidth: '220px' }}>
                      <div className="px-4 py-3 border-b" style={{ borderColor: '#E0E0E0' }}>
                        <p className="text-xs" style={{ color: '#6B6B6B' }}>Signed in as</p>
                        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>{userName || 'Robert'}</p>
                      </div>
                      <a
                        href="/api/auth/admin-direct/enroll"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-orange-50 transition-colors"
                        style={{ color: '#333333' }}
                        data-testid="admin-enroll-passkey"
                      >
                        <Lock size={14} style={{ color: '#F5831F' }} />
                        Fingerprint / Face ID…
                      </a>
                      <button
                        onClick={() => { setShowUserMenu(false); handleAdminNav('/dashboard'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left"
                        style={{ color: '#333333' }}
                        data-testid="admin-back-to-app"
                      >
                        <Home size={14} style={{ color: '#6B6B6B' }} />
                        Back to App
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors text-left border-t"
                        style={{ color: '#B91C1C', borderColor: '#E0E0E0' }}
                        data-testid="admin-sign-out"
                      >
                        <LogOut size={14} />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <main className="p-3 sm:p-6 min-w-0 max-w-full overflow-x-hidden">{children}</main>
        </div>
      </div>

      {/* Scroll-to-top floating button — appears once user scrolls > 320px.
          Sits on the right edge, above the page content, never overlaps the
          sticky header. */}
      <button
        type="button"
        onClick={scrollAllToTop}
        aria-label="Scroll to top"
        data-testid="admin-scroll-to-top"
        className={`fixed bottom-6 right-6 z-[60] w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${showScrollTop ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'}`}
        style={{ backgroundColor: '#1A1209', color: '#F5831F', border: '1px solid #F5831F' }}
      >
        <ArrowUp size={18} />
      </button>

      {/* Command Palette Modal */}
      <CommandPalette isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} />

      {/* Responsive Preview Modal */}
      <ResponsivePreview isOpen={showPreview} onClose={() => setShowPreview(false)} />
    </DateRangeContext.Provider>
  );
};

export default AdminLayout;
